<template>
  <div class="dash-card financial-summary-widget">
    <div class="dash-card-header">
      <h3 class="dash-card-title">
        <span
          class="dash-card-title-icon"
          style="background: #e6f7ff; color: #1890ff"
        >
          <a-icon type="wallet" />
        </span>
        {{ $t("accueil.dashboard.financialSummary") }}
      </h3>
      <span class="dash-card-action" @click="goToEtatDesRecettes">
        {{ $t("accueil.dashboard.seeDetails") }}
        <a-icon type="right" />
      </span>
    </div>

    <div class="dash-card-body">
      <div v-if="loading" class="finance-loading">
        <a-spin />
      </div>
      <template v-else>
      <div class="finance-row">
        <div class="finance-block finance-block--in">
          <div class="finance-block-icon">
            <a-icon type="arrow-up" />
          </div>
          <div class="finance-block-content">
            <div class="finance-label">
              {{ $t("accueil.dashboard.encaissementDuJour") }}
            </div>
            <div class="finance-value">
              {{ formatAmount(encaissement) }}
              <span class="finance-currency">{{ currency }}</span>
            </div>
            <div
              class="finance-trend"
              :class="encaissementTrend >= 0 ? 'is-up' : 'is-down'"
            >
              <a-icon :type="encaissementTrend >= 0 ? 'caret-up' : 'caret-down'" />
              {{ formatTrend(encaissementTrend) }}%
              <span class="finance-trend-label">{{
                $t("accueil.dashboard.vsHier")
              }}</span>
            </div>
          </div>
        </div>

        <div class="finance-divider" />

        <div class="finance-block finance-block--out">
          <div class="finance-block-icon">
            <a-icon type="arrow-down" />
          </div>
          <div class="finance-block-content">
            <div class="finance-label">
              {{ $t("accueil.dashboard.decaissementDuJour") }}
            </div>
            <div class="finance-value">
              {{ formatAmount(decaissement) }}
              <span class="finance-currency">{{ currency }}</span>
            </div>
            <div
              class="finance-trend"
              :class="decaissementTrend <= 0 ? 'is-up' : 'is-down'"
            >
              <a-icon
                :type="decaissementTrend <= 0 ? 'caret-down' : 'caret-up'"
              />
              {{ formatTrend(decaissementTrend) }}%
              <span class="finance-trend-label">{{
                $t("accueil.dashboard.vsHier")
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="finance-net" :class="net >= 0 ? 'is-positive' : 'is-negative'">
        <a-icon :type="net >= 0 ? 'rise' : 'fall'" />
        <span>{{ $t("accueil.dashboard.soldeNetJour") }}</span>
        <strong>{{ formatAmount(net) }} {{ currency }}</strong>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getBuildingCurrency } from "@/utilities/countrySpecific";
import apiClient from "@/services/axios";

const emptySummary = () => ({
  encaissementDuJour: { totalReceived: 0 },
  decaissementDuJour: { totalToPay: 0 },
  encaissementHier: { totalReceived: 0 },
  decaissementHier: { totalToPay: 0 },
});

export default {
  name: "FinancialSummaryWidget",
  data() {
    return {
      currency: getBuildingCurrency(),
      loading: false,
      financialSummary: emptySummary(),
    };
  },
  computed: {
    encaissement() {
      return Number(this.financialSummary?.encaissementDuJour?.totalReceived) || 0;
    },
    decaissement() {
      return Number(this.financialSummary?.decaissementDuJour?.totalToPay) || 0;
    },
    net() {
      return this.encaissement - this.decaissement;
    },
    encaissementTrend() {
      return this.computeTrend(
        this.encaissement,
        Number(this.financialSummary?.encaissementHier?.totalReceived) || 0
      );
    },
    decaissementTrend() {
      return this.computeTrend(
        this.decaissement,
        Number(this.financialSummary?.decaissementHier?.totalToPay) || 0
      );
    },
  },
  created() {
    this.loadFinancialSummary();
  },
  methods: {
    async loadFinancialSummary() {
      this.loading = true;
      try {
        const { data } = await apiClient.get("/home/financial-summary");
        this.financialSummary = data || emptySummary();
      } catch (error) {
        console.error("Error loading financial summary:", error);
        this.financialSummary = emptySummary();
      } finally {
        this.loading = false;
      }
    },
    goToEtatDesRecettes() {
      if (this.$route.path !== "/encaissement/etatDesRecettes") {
        this.$router.push({ path: "/encaissement/etatDesRecettes" });
      }
    },
    computeTrend(current, previous) {
      if (!previous) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    },
    formatTrend(value) {
      const rounded = Math.round(Math.abs(value) * 10) / 10;
      return rounded;
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString();
    },
  },
};
</script>

<style lang="scss" scoped>
.finance-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

.finance-row {
  display: flex;
  align-items: stretch;
  gap: 18px;
}

.finance-divider {
  width: 1px;
  background: #f0f0f0;
}

.finance-block {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.finance-block-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.finance-block--in .finance-block-icon {
  background: #f6ffed;
  color: var(--dash-success);
}

.finance-block--out .finance-block-icon {
  background: #fff1f0;
  color: var(--dash-danger);
}

.finance-block-content {
  flex: 1;
  min-width: 0;
}

.finance-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.finance-value {
  font-size: 1.55rem;
  font-weight: 800;
  color: #1a1a1a;
  line-height: 1.1;
}

.finance-currency {
  font-size: 0.8rem;
  font-weight: 600;
  color: #8c8c8c;
}

.finance-trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 0.78rem;
  font-weight: 700;

  &.is-up {
    color: var(--dash-success);
  }
  &.is-down {
    color: var(--dash-danger);
  }

  .finance-trend-label {
    color: #8c8c8c;
    font-weight: 500;
    margin-left: 2px;
  }
}

.finance-net {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e5e7eb;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #595959;

  strong {
    margin-left: auto;
    font-size: 1rem;
  }

  &.is-positive {
    color: var(--dash-success);
  }
  &.is-negative {
    color: var(--dash-danger);
  }
}

@media (max-width: 480px) {
  .finance-row {
    flex-direction: column;
  }
  .finance-divider {
    display: none;
  }
}

[data-kit-theme="dark"] {
  .finance-divider {
    background: #232135;
  }

  .finance-block--in .finance-block-icon {
    background: rgba(82, 196, 26, 0.15);
  }

  .finance-block--out .finance-block-icon {
    background: rgba(245, 34, 45, 0.15);
  }

  .finance-value {
    color: #aeaee0;
  }

  .finance-net {
    border-top-color: #232135;
  }
}
</style>
